import React, { useState } from 'react';
import RatingStars from './RatingStars';
import { reviewsAPI } from '../../utils/api';
import { toast } from 'sonner';
import { Shield, ThumbsUp, X } from 'lucide-react';

const getVoterId = () => {
  let id = localStorage.getItem('sc_voter_id');
  if (!id) {
    id = `voter_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    localStorage.setItem('sc_voter_id', id);
  }
  return id;
};

const ReviewCard = ({ review }) => {
  const [helpfulCount, setHelpfulCount] = useState(review.helpful_count);
  const [voted, setVoted] = useState(false);
  const [lightboxImg, setLightboxImg] = useState(null);

  const handleHelpful = async () => {
    if (voted) return;
    try {
      await reviewsAPI.markHelpful(review.id, getVoterId());
      setHelpfulCount(prev => prev + 1);
      setVoted(true);
    } catch (_) {
      toast.error('Already voted or error occurred');
    }
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const days = Math.floor(diff / 86400000);
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 30) return `${days} days ago`;
    if (days < 365) return `${Math.floor(days / 30)} months ago`;
    return `${Math.floor(days / 365)} years ago`;
  };

  return (
    <div className="border-b border-[#EBE4D5] pb-6 mb-6 last:border-0 last:mb-0" data-testid={`review-card-${review.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <RatingStars rating={review.rating} size="sm" />
            {review.is_verified_purchase && (
              <span className="flex items-center gap-1 text-[#3A5A40] font-outfit text-xs" data-testid="verified-badge">
                <Shield className="w-3 h-3" /> Verified Purchase
              </span>
            )}
          </div>
          {review.title && <h4 className="font-outfit font-medium text-[#1A1A18] mb-1">{review.title}</h4>}
        </div>
        <span className="font-outfit text-xs text-[#8C8577] whitespace-nowrap">{timeAgo(review.created_at)}</span>
      </div>

      <p className="font-outfit text-sm text-[#4A463F] leading-relaxed mb-3">{review.text}</p>

      {/* Photos & Videos */}
      {(review.photos?.length > 0 || review.videos?.length > 0) && (
        <div className="flex gap-2 flex-wrap mb-3">
          {review.photos?.map((p) => (
            <button key={p} onClick={() => setLightboxImg(`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`)} className="w-16 h-16 rounded overflow-hidden border border-[#EBE4D5] hover:border-[#C29B57] transition-colors">
              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`} alt="Review photo" className="w-full h-full object-cover" loading="lazy" />
            </button>
          ))}
          {review.videos?.map((v) => (
            <div key={v} className="w-16 h-16 rounded overflow-hidden border border-[#EBE4D5]">
              <video src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${v}`} className="w-full h-full object-cover" controls={false} onClick={(e) => { e.target.controls = true; e.target.play(); }} />
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between">
        <span className="font-outfit text-xs text-[#8C8577]">{review.customer_name}</span>
        <button
          onClick={handleHelpful}
          disabled={voted}
          className={`flex items-center gap-1.5 font-outfit text-xs transition-colors ${voted ? 'text-[#C29B57]' : 'text-[#8C8577] hover:text-[#C29B57]'}`}
          data-testid={`helpful-${review.id}`}
        >
          <ThumbsUp className="w-3.5 h-3.5" />
          Helpful ({helpfulCount})
        </button>
      </div>

      {/* Lightbox */}
      {lightboxImg && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4" onClick={() => setLightboxImg(null)}>
          <button className="absolute top-4 right-4 text-white" onClick={() => setLightboxImg(null)}><X className="w-8 h-8" /></button>
          <img src={lightboxImg} alt="Review" className="max-w-full max-h-[90vh] object-contain rounded-lg" />
        </div>
      )}
    </div>
  );
};

export default ReviewCard;
